/**
 * EN-1569: the backend's live push for a pig run's user notification list.
 *
 * When anyone saves a run's list (progress card > Notifications > Add or
 * Delete, which save straight away since EN-1570; PUT /pigrun/{id}/notifications), the AgmHub sends
 * ReceivePigRunNotificationsChanged { runId } to the run's own group. The
 * message only says which run changed; this browser re-reads what it shows
 * through the normal, auth-checked GETs:
 * - the two viewer lists ("active runs / AGMs I am subscribed to"), so a
 *   viewer just added to or removed from the run sees it appear or go;
 * - the list in a progress card whose Notifications dialog is open (always
 *   re-read and replaced: Add and Delete save straight away, so there are no
 *   unsaved edits to keep).
 * Other users also get a short toast. The browser that saved gets the push
 * too; it refreshes quietly, without the toast.
 */

/** SignalR event sent by the AgmHub when a pig run's notification list was saved. */
export const PIG_RUN_NOTIFICATIONS_CHANGED_EVENT =
  'ReceivePigRunNotificationsChanged';

/** Vuex action dispatched for that event (debounced per run). */
export const PIG_RUN_NOTIFICATIONS_CHANGED_ACTION =
  'pigRunNotificationsChangedReceived';

/** Signal-only Vuex action: the cards showing { runId } re-read their list. */
export const PIG_RUN_NOTIFICATIONS_REFRESH_ACTION =
  'pigRunNotificationsRefresh';

/** Pushes for one run within this window make one refresh. */
export const PIG_RUN_NOTIFICATIONS_PUSH_DELAY_MS = 1000;

/** The browser that saved a run's list shows no toast for this long. */
export const JUST_SAVED_WINDOW_MS = 5000;

/** Toast wording when the run has no name. */
export const NOTIFICATIONS_CHANGED_MESSAGE =
  'Notifications for this pig run were changed.';

/** Payload of ReceivePigRunNotificationsChanged (PigRunNotificationsChangedMsg). */
export interface PigRunNotificationsChangedMsg {
  runId: number;
  timestampUtc?: string;
}

interface HubConnectionLike {
  on(eventName: string, handler: (data: any) => void): void;
}

/** What reloadOpenNotificationsDialog did with a push. */
export type NotificationsDialogReload =
  | 'closed'
  | 'reloaded'
  | 'dropped'
  | 'failed';

/** True for a payload with a positive whole run id. */
export function isPigRunNotificationsChangedMsg(
  data: unknown
): data is PigRunNotificationsChangedMsg {
  if (data == null || typeof data !== 'object') return false;
  const runId = (data as { runId?: unknown }).runId;
  return typeof runId === 'number' && Number.isInteger(runId) && runId > 0;
}

/** The toast text, with the run's name when there is one. */
export function notificationsChangedMessage(runName?: unknown): string {
  const name = typeof runName === 'string' ? runName.trim() : '';
  return name
    ? `Notifications for pig run ${name} were changed.`
    : NOTIFICATIONS_CHANGED_MESSAGE;
}

/**
 * Registers the ReceivePigRunNotificationsChanged handler on the AgmHub
 * connection. A bad payload is ignored. The hub already sends to the run's
 * group only.
 */
export function registerPigRunNotificationsChangedHandler(
  connection: HubConnectionLike,
  dispatch: (type: string, payload: PigRunNotificationsChangedMsg) => void
): void {
  connection.on(PIG_RUN_NOTIFICATIONS_CHANGED_EVENT, (data: unknown) => {
    if (!isPigRunNotificationsChangedMsg(data)) return;
    dispatch(PIG_RUN_NOTIFICATIONS_CHANGED_ACTION, data);
  });
}

// When this browser last saved each run's list (run id -> ms).
const savedHereAt = new Map<number, number>();

/** Called by the progress card when this browser saves a run's list. */
export function markPigRunNotificationsSavedHere(
  runId: number,
  now: number = Date.now()
): void {
  savedHereAt.set(runId, now);
}

/** True when this browser saved the run's list in the last 5 s. */
export function wasPigRunNotificationsSavedHere(
  runId: number,
  now: number = Date.now()
): boolean {
  const at = savedHereAt.get(runId);
  if (at == null) return false;
  if (now - at > JUST_SAVED_WINDOW_MS) {
    savedHereAt.delete(runId);
    return false;
  }
  return true;
}

/** Forgets every "saved here" marker (unit specs). */
export function clearPigRunNotificationsSavedHere(): void {
  savedHereAt.clear();
}

/**
 * A progress card's handling of a push for its run. A closed dialog loads the
 * list when it opens, so nothing is needed ('closed'). An open dialog always
 * re-reads the list and replaces it ('reloaded'): since EN-1570 every Add and
 * Delete saves straight away, so there are no unsaved edits to keep. A result
 * that arrives after the dialog closed or the card moved to another run is
 * dropped; a failed read keeps the list on screen (reopening loads it again).
 */
export async function reloadOpenNotificationsDialog<T>(
  getState: () => { dialogOpen: boolean; runId: number | null | undefined },
  load: (runId: number) => Promise<T[]>,
  apply: (list: T[]) => void
): Promise<NotificationsDialogReload> {
  const { dialogOpen, runId } = getState();
  if (!dialogOpen || !runId) return 'closed';
  let list: T[];
  try {
    list = await load(runId);
  } catch (err) {
    console.error('Failed to re-read notifications:', err);
    return 'failed';
  }
  const now = getState();
  if (!now.dialogOpen || now.runId !== runId) return 'dropped';
  apply(list);
  return 'reloaded';
}

/** The map's viewer rule: a viewer only sees the AGMs of runs they are on. */
export function viewerMaySeeUid(
  isViewer: boolean,
  allowedUids: readonly number[] | null | undefined,
  uid: number | string
): boolean {
  if (!isViewer) return true;
  if (!Array.isArray(allowedUids)) return false;
  return allowedUids.some((allowed) => allowed == uid);
}

/** The AGM icons on a viewer's map that the viewer may no longer see. */
export function viewerUidsToRemove(
  isViewer: boolean,
  shownUids: readonly (number | string)[],
  allowedUids: readonly number[] | null | undefined
): number[] {
  if (!isViewer) return [];
  return shownUids
    .filter((uid) => !viewerMaySeeUid(true, allowedUids, uid))
    .map(Number);
}
